import React from 'react';
import OrderDraftCard from './OrderDraftCard';

/** Conversation body size — bubbles, input, and markdown inherit this. */
export const AI_CHAT_FONT_SIZE = 16;
export const AI_CHAT_LINE_HEIGHT = 1.65;

/**
 * Shared ReactMarkdown component map for PrecisionAI bubbles (narrow sidebar).
 */
export function createAiChatMarkdownComponents(colors) {
  const border = colors.borderLight || colors.border;
  return {
    p: ({ children }) => <p style={{ margin: '0 0 14px', lineHeight: AI_CHAT_LINE_HEIGHT }}>{children}</p>,
    ul: ({ children }) => <ul style={{ margin: '6px 0 16px', paddingLeft: '1.15em', lineHeight: AI_CHAT_LINE_HEIGHT }}>{children}</ul>,
    ol: ({ children }) => <ol style={{ margin: '6px 0 16px', paddingLeft: '1.25em', lineHeight: AI_CHAT_LINE_HEIGHT }}>{children}</ol>,
    li: ({ children }) => <li style={{ marginBottom: '8px' }}>{children}</li>,
    strong: ({ children }) => (
      <strong style={{ color: colors.white, fontWeight: '600' }}>{children}</strong>
    ),
    em: ({ children }) => <em style={{ opacity: 0.85 }}>{children}</em>,
    h1: ({ children }) => (
      <p style={{ margin: '20px 0 8px', fontWeight: '650', fontSize: '17px', lineHeight: 1.35 }}>{children}</p>
    ),
    h2: ({ children }) => (
      <p style={{ margin: '20px 0 8px', fontWeight: '650', fontSize: '16px', lineHeight: 1.35 }}>{children}</p>
    ),
    h3: ({ children }) => (
      <p style={{ margin: '16px 0 6px', fontWeight: '650', fontSize: '15px', lineHeight: 1.4 }}>{children}</p>
    ),
    hr: () => (
      <hr style={{ border: 'none', borderTop: `1px solid ${border}`, margin: '16px 0' }} />
    ),
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: '10px 0 16px', maxWidth: '100%' }} className="ai-chat-table-wrap">
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '15px',
            lineHeight: 1.5,
          }}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead>{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th
        style={{
          border: `1px solid ${border}`,
          padding: '8px 10px',
          textAlign: 'left',
          backgroundColor: colors.bgPrimary,
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        style={{
          border: `1px solid ${border}`,
          padding: '8px 10px',
          verticalAlign: 'top',
        }}
      >
        {children}
      </td>
    ),
    code: ({ className, children, ...props }) => {
      if (className === 'language-order-draft') {
        let draft = null;
        try {
          draft = JSON.parse(String(children).trim());
        } catch {
          return null;
        }
        return <OrderDraftCard draft={draft} colors={colors} />;
      }
      return (
        <code
          className={className}
          style={{
            background: colors.bgPrimary,
            border: `1px solid ${border}`,
            borderRadius: 4,
            padding: '0.1em 0.35em',
            fontSize: '0.9em',
          }}
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }) => {
      // order-draft blocks render as a card above, not a <pre> wrapper.
      const inner = React.Children.toArray(children)[0];
      if (inner?.props?.className === 'language-order-draft') return <>{children}</>;
      return (
        <pre style={{ overflowX: 'auto', margin: '6px 0 16px', padding: '10px 12px', background: colors.bgPrimary, border: `1px solid ${border}`, borderRadius: 8 }}>
          {children}
        </pre>
      );
    },
  };
}
